import { createFileRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useFeedbackContext } from '@/contexts/FeedbackContext'
import { BusinessCreateOrEditPendingComponent } from '@/features/admin/businesses/create-edit/components/BusinessCreateOrEditPendingComponent'
import { BusinessForm } from '@/features/admin/businesses/create-edit/components/BusinessForm'
import { businessFormOptions } from '@/features/admin/businesses/create-edit/helper/businessFormOptions'
import { useUpdateBusiness } from '@/features/admin/businesses/create-edit/mutations/useUpdateBusiness'
import { assertUserIsAdminOrRedirect } from '@/features/admin/helper/assertUserIsAdminOrRedirect'
import { DeletedItemSelection } from '@/generated/Enumerations/DeletedItemSelection_pb'
import { useAppForm } from '@/hooks/useAppForm'
import {
    businessDetailsQueryOptions,
    useBusinessDetails,
} from '@/queries/useBusinessDetails'

const seleniumPrefix = 'edit-business'

const RouteComponent: React.FC = () => {
    const { id } = Route.useParams()
    const { data: businessDetails, refetch } = useBusinessDetails(
        id,
        DeletedItemSelection.NORMAL_ONLY
    )
    const updateBusiness = useUpdateBusiness()
    const { t } = useTranslation()
    const { addFeedback } = useFeedbackContext()
    const form = useAppForm({
        ...businessFormOptions(businessDetails),

        onSubmitInvalid: () => {
            addFeedback?.({
                variant: 'snackbar',
                severity: 'warning',
                title: t('snackbar.failedValidation'),
            })
        },

        onSubmit: async ({ value, formApi }) => {
            await updateBusiness.mutateAsync({
                ...value,
                id,
                oldBusinessLocations: businessDetails.businessLocations,
            })

            await refetch()

            formApi.reset()
        },
    })

    return (
        <>
            <title>{t('business.urlEdit')}</title>

            <form.AppForm>
                <form.AdminForm
                    title={t('business.edit')}
                    backTo={'/admin/businesses'}
                >
                    <form.Blocker data-selenium={`${seleniumPrefix}-blocker`} />

                    <BusinessForm form={form} />
                </form.AdminForm>
            </form.AppForm>
        </>
    )
}

const PendingComponent: React.FC = () => {
    const { t } = useTranslation()
    return <BusinessCreateOrEditPendingComponent title={t('business.edit')} />
}

export const Route = createFileRoute(
    '/__base-layout/admin/businesses/$id/edit'
)({
    component: RouteComponent,
    beforeLoad: async ({
        params: { id },
        context: { user, queryClient, grpcClients },
    }) => {
        assertUserIsAdminOrRedirect(user)

        await Promise.all([
            queryClient.ensureQueryData(
                businessDetailsQueryOptions(
                    grpcClients,
                    id,
                    DeletedItemSelection.NORMAL_ONLY
                )
            ),
        ])
    },
    pendingComponent: PendingComponent,
})
